/*
 * Copyright 2024 GLA UK Research and Development Directive
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { NgxSelectModule } from 'ngx-select-ex';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { environment } from 'src/environments/environment';
import { SharedModule } from './modules/shared/shared.module';
import { HomeModule } from './modules/home/home.module';
import { MessagesModule } from './modules/messages/messages.module';
import { AtonsModule } from './modules/atons/atons.module';
import { EditorModule } from './modules/editor/editor.module'
import { AdminModule } from './modules/admin/admin.module';

import { AuthInterceptor } from './core/auth/auth.interceptor';
import { DomainService } from './modules/shared/services/domain.service';
import { LanguageService } from './modules/shared/services/language.service';
import { AnalyticsService } from './modules/shared/services/analytics.service';
import { TranslationLoaderService } from './modules/shared/services/translation-loader.service';

import { initializer } from './core/utils/app-init';
import { BaseApiUrlInterceptor } from './core/utils/base-api-url.interceptor';
import { HeaderBarComponent } from './core/header-bar/header-bar.component';
import { FooterBarComponent } from './core/footer-bar/footer-bar.component';
import { BrowserCheckComponent } from './core/browser-check/browser-check.component';

/**
 * The Application Module.
 */
@NgModule({ declarations: [
        AppComponent,
        HeaderBarComponent,
        FooterBarComponent,
        BrowserCheckComponent
    ],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        KeycloakAngularModule,
        TranslateModule.forRoot({
            defaultLanguage: 'en',
            loader: {
                provide: TranslateLoader,
                useClass: TranslationLoaderService,
                deps: [HttpClient]
            }
        }),
        NgbModule,
        NgxSelectModule.forRoot({ keepSelectedItems: true, keyCodeToOptionsOpen: 'ArrowDown' }),
        NgxMaskDirective,
        NgxMaskPipe,
        FontAwesomeModule,
        SharedModule,
        HomeModule,
        AtonsModule,
        MessagesModule,
        EditorModule,
        AdminModule
    ],
    providers: [
        provideNgxMask(),
        provideHttpClient(withInterceptorsFromDi()),
        provideAppInitializer(() => {
        const initializerFn = (initializer)(inject(KeycloakService), inject(DomainService), inject(LanguageService));
        return initializerFn();
      }), {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        }, {
            provide: HTTP_INTERCEPTORS,
            useClass: BaseApiUrlInterceptor,
            multi: true
        }, {
            provide: "BASE_API_URL", useValue: environment.baseApiUrl
        },
        AnalyticsService
    ]})
export class AppModule { }
